import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  Plus, Edit, Trash2, Save, X, Calendar, 
  MapPin, Award, ExternalLink, Loader
} from 'lucide-react';
import './ArtistExhibition.css';

const API_URL = 'https://my-musing-backend.onrender.com/api';

const ArtistExhibition = ({ 
  exhibitions, 
  fetchData, 
  viewport 
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingExhibition, setEditingExhibition] = useState(null);
  const [exhibitionsPage, setExhibitionsPage] = useState(1);
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  
  const [exhibitionForm, setExhibitionForm] = useState({
    title: '',
    organizer: '',
    location: '',
    year: new Date().getFullYear(),
    type: 'Group',
    description: '',
    website: ''
  });

  const itemsPerPage = useMemo(() => ({
    mobile: 6,
    tablet: 9,
    desktop: 12
  }), []);

  const getItemsPerPage = useCallback(() => {
    if (viewport.isMobile) return itemsPerPage.mobile;
    if (viewport.isTablet) return itemsPerPage.tablet;
    return itemsPerPage.desktop;
  }, [viewport.isMobile, viewport.isTablet, itemsPerPage]);

  const displayedExhibitions = useMemo(() => {
    const itemsCount = exhibitionsPage * getItemsPerPage();
    return exhibitions.slice(0, itemsCount);
  }, [exhibitions, exhibitionsPage, getItemsPerPage]);

  const hasMoreExhibitions = useMemo(() => 
    displayedExhibitions.length < exhibitions.length,
    [displayedExhibitions.length, exhibitions.length]
  );

  const handleShowMore = useCallback(() => {
    setShowMoreLoading(true);
    setTimeout(() => {
      setExhibitionsPage(prev => prev + 1);
      setShowMoreLoading(false);
      
      setTimeout(() => {
        const currentItems = exhibitionsPage * getItemsPerPage();
        const exhibitionCards = document.querySelectorAll('.exhibition-card-dashboard');
        if (exhibitionCards[currentItems]) {
          exhibitionCards[currentItems].scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest' 
          });
        }
      }, 100);
    }, 500);
  }, [exhibitionsPage, getItemsPerPage]);

  const resetForm = () => {
    setExhibitionForm({
      title: '',
      organizer: '',
      location: '',
      year: new Date().getFullYear(),
      description: '',
      website: ''
    });
    setEditingExhibition(null);
  };

  const handleEdit = (exhibition) => {
    setEditingExhibition(exhibition);
    setExhibitionForm({
      title: exhibition.title,
      organizer: exhibition.organizer,
      location: exhibition.location,
      year: exhibition.year,
      description: exhibition.description || '',
      website: exhibition.website || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this exhibition?')) return;

    try {
      await axios.delete(`${API_URL}/exhibitions/${id}`);
      alert('Exhibition deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting exhibition:', error);
      alert('Error deleting exhibition');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...exhibitionForm,
        year: parseInt(exhibitionForm.year)
      };

      if (editingExhibition) {
        await axios.put(`${API_URL}/exhibitions/${editingExhibition.id}`, payload);
        alert('Exhibition updated successfully!');
      } else {
        await axios.post(`${API_URL}/exhibitions`, payload);
        alert('Exhibition added successfully!');
      }
      
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving exhibition:', error);
      alert('Error saving exhibition: ' + (error.response?.data?.message || error.message));
    }
  };

  const groupExhibitionsByYear = () => {
    const grouped = {};
    displayedExhibitions.forEach(exhibition => {
      if (!grouped[exhibition.year]) {
        grouped[exhibition.year] = [];
      }
      grouped[exhibition.year].push(exhibition);
    });
    return grouped;
  };

  const groupedExhibitions = groupExhibitionsByYear();
  const years = Object.keys(groupedExhibitions).sort((a, b) => b - a);

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div>
          <h2>Manage Exhibitions</h2>
          <p>Track your exhibition history and artistic journey ({displayedExhibitions.length} of {exhibitions.length})</p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            resetForm();
          }}
          className="btn-primary01"
        >
          <Plus size={20} />
          Add Exhibition
        </button>
      </div>

      {exhibitions.length === 0 ? (
        <div className="no-items-dashboard">
          <Calendar size={64} />
          <h3>No Exhibitions Yet</h3>
          <p>Start documenting your exhibition journey</p>
        </div>
      ) : (
        <>
          <div className="exhibitions-timeline-dashboard">
            {years.map((year) => (
              <div key={year} className="year-group-dashboard">
                <div className="year-header-dashboard">
                  <Calendar size={24} />
                  <h3>{year}</h3>
                  <span className="year-count">{groupedExhibitions[year].length}</span>
                </div>

                <div className="exhibitions-grid-dashboard">
                  {groupedExhibitions[year].map((exhibition, index) => (
                    <div
                      key={exhibition.id}
                      className="exhibition-card-dashboard"
                      style={{
                        animationDelay: `${index * 0.05}s`
                      }}
                    >
                      <div className="exhibition-header-dashboard">
                        <div className="exhibition-icon-dashboard">
                          <Award size={20} />
                        </div>
                      </div>

                      <div className="exhibition-body-dashboard">
                        <h4 className="exhibition-title-dashboard">
                          {exhibition.title}
                        </h4>

                        <div className="exhibition-info-dashboard">
                          <div className="info-row">
                            <Award size={14} />
                            <span>{exhibition.organizer}</span>
                          </div>
                          <div className="info-row">
                            <MapPin size={14} />
                            <span>{exhibition.location}</span>
                          </div>
                        </div>

                        {exhibition.description && (
                          <p className="exhibition-desc-dashboard">
                            {exhibition.description}
                          </p>
                        )}

                        {exhibition.website && (
                          <a
                            href={exhibition.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="exhibition-website"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink size={14} />
                            <span>View Website</span>
                          </a>
                        )}
                      </div>

                      <div className="exhibition-actions-dashboard">
                        <button
                          onClick={() => handleEdit(exhibition)}
                          className="btn-secondary01"
                        >
                          <Edit size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(exhibition.id)}
                          className="btn-danger01"
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {hasMoreExhibitions && (
            <div className="show-more-container">
              <div className="pagination-info">
                Showing {displayedExhibitions.length} of {exhibitions.length} exhibitions
              </div>
              <button 
                className="show-more-btn"
                onClick={handleShowMore}
                disabled={showMoreLoading}
                type="button"
              >
                {showMoreLoading ? (
                  <>
                    <Loader className="spinner" size={20} />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <Plus size={20} />
                    <span>Load More ({Math.min(getItemsPerPage(), exhibitions.length - displayedExhibitions.length)} items)</span>
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Exhibition Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowForm(false)}>
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <h3>{editingExhibition ? 'Edit Exhibition' : 'Add New Exhibition'}</h3>
              <p>Document your exhibition journey</p>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Exhibition Title *</label>
                <input
                  type="text"
                  required
                  value={exhibitionForm.title}
                  className="text-input"
                  onChange={(e) => setExhibitionForm({ ...exhibitionForm, title: e.target.value })}
                  placeholder="Exhibition Title *"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Organizer *</label>
                  <input
                    type="text"
                    required
                    value={exhibitionForm.organizer}
                    className="text-input"
                    onChange={(e) => setExhibitionForm({ ...exhibitionForm, organizer: e.target.value })}
                    placeholder="Organizer: e.g., Art Tools Gallery *"
                  />
                </div>

                <div className="form-group">
                  <label>Location </label>
                  <input
                    type="text"
                    value={exhibitionForm.location}
                    className="text-input"
                    onChange={(e) => setExhibitionForm({ ...exhibitionForm, location: e.target.value })}
                    placeholder="Location:e.g., ICCR, Kolkata"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Year *</label>
                  <input
                    type="number"
                    required
                    min="1900"
                    max={new Date().getFullYear() + 10}
                    value={exhibitionForm.year}
                    className="text-input"
                    placeholder="Year *"
                    onChange={(e) => setExhibitionForm({ ...exhibitionForm, year: e.target.value })}
                  />
                </div>

                {/* <div className="form-group">
                  <label>Type *</label>
                  <select
                    required
                    value={exhibitionForm.type}
                    className="text-input"
                    onChange={(e) => setExhibitionForm({ ...exhibitionForm, type: e.target.value })}
                  >
                    <option value="Solo">Solo Exhibition</option>
                    <option value="Group">Group Exhibition</option>
                    <option value="International">International</option>
                    <option value="National">National</option>
                    <option value="Online">Online Exhibition</option>
                    <option value="Competition">Competition</option>
                  </select>
                </div> */}
              </div>

              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  rows="3"
                  value={exhibitionForm.description}
                  className="text-input"
                  onChange={(e) => setExhibitionForm({ ...exhibitionForm, description: e.target.value })}
                  placeholder="Add details about the exhibition, awards, or recognition..."
                />
              </div>

              <div className="form-group">
                <label>Website URL (Optional)</label>
                <input
                  type="url"
                  value={exhibitionForm.website}
                  className="text-input"
                  onChange={(e) => setExhibitionForm({ ...exhibitionForm, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  <Save size={20} />
                  {editingExhibition ? 'Update' : 'Add'} Exhibition
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  <X size={20} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistExhibition;